<script setup>
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import { toastError, toastSaved } from '@js/utils/toast';
import { ref, watch } from 'vue';

const {
    number,
    url,
    fieldName = 'value',
    payload = {},
    min = null,
    max = null,
    step = 1,
    integer = false,
    align = 'left',
    disabled = false,
    label = null,
    suffix = null,
    callback,
} = defineProps({
    number: {
        type: Number,
        required: true,
    },
    url: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: false,
    },
    payload: {
        type: Object,
        required: false,
    },
    min: {
        type: Number,
        required: false,
    },
    max: {
        type: Number,
        required: false,
    },
    step: {
        type: Number,
        required: false,
    },
    integer: {
        type: Boolean,
        required: false,
    },
    align: {
        type: String,
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    label: {
        type: String,
        required: false,
    },
    suffix: {
        type: String,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const emit = defineEmits(['saved']);

const value = ref(number);

const saving = ref(false);

watch(
    () => number,
    (newNumber) => {
        value.value = newNumber;
    },
);

function isValid(newNumber) {
    if (!Number.isFinite(newNumber)) {
        return false;
    }

    if (integer && !Number.isInteger(newNumber)) {
        return false;
    }

    if (min !== null && newNumber < min) {
        return false;
    }

    return !(max !== null && newNumber > max);
}

function save() {
    const newNumber = Number.parseFloat(value.value);

    if (!isValid(newNumber)) {
        value.value = number;

        return;
    }

    if (newNumber === number) {
        value.value = newNumber;

        return;
    }

    saving.value = true;

    axios
        .post(url, {
            ...payload,
            [fieldName]: newNumber,
        })
        .then((response) => {
            toastSaved();

            emit('saved', newNumber);

            if (callback) {
                callback(newNumber, response);
            }
        })
        .catch((error) => {
            value.value = number;

            toastError(error);
        })
        .finally(() => {
            saving.value = false;
        });
}
</script>

<template>
    <BaseCell :align="align">
        <input
            v-model="value"
            type="number"
            :min="min ?? undefined"
            :max="max ?? undefined"
            :step="step"
            :disabled="disabled || saving"
            :aria-label="label ?? undefined"
            class="h-8 w-20 rounded-sm border border-gray-300 bg-white px-2 py-1 text-sm ring-1 ring-transparent duration-150 focus:ring-gray-300 focus:outline-hidden disabled:opacity-50"
            @change="save"
            @keyup.enter="(event) => event.target.blur()"
        />

        <span v-if="suffix" class="text-sm">{{ suffix }}</span>
    </BaseCell>
</template>
